<template>
  <section class="news-section">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">新闻动态</h2>
        <p class="section-subtitle">了解我们的最新动态和行业资讯</p>
      </div>

      <div class="news-content">
        <!-- 推荐新闻 -->
        <div class="featured-news">
          <h3 class="subsection-title">推荐阅读</h3>
          <div class="featured-grid">
            <div
              v-for="news in featuredNews"
              :key="news.id"
              class="featured-item"
              @click="goToNewsDetail(news.id)"
            >
              <div class="featured-image">
                <img
                  :src="news.coverImage || '/images/news-default.jpg'"
                  :alt="news.title"
                  @error="handleImageError"
                >
                <div class="featured-overlay">
                  <span class="featured-tag">推荐</span>
                </div>
              </div>
              <div class="featured-content">
                <h4 class="featured-title">{{ news.title }}</h4>
                <p class="featured-summary">{{ news.summary }}</p>
                <div class="featured-meta">
                  <span class="news-date">{{ formatDate(news.publishTime) }}</span>
                  <span class="news-category">{{ news.category }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 最新新闻 -->
        <div class="latest-news">
          <h3 class="subsection-title">最新动态</h3>
          <div class="news-list">
            <div
              v-for="news in latestNews"
              :key="news.id"
              class="news-item"
              @click="goToNewsDetail(news.id)"
            >
              <div class="news-image">
                <img
                  :src="news.coverImage || '/images/news-default.jpg'"
                  :alt="news.title"
                  @error="handleImageError"
                >
              </div>
              <div class="news-content">
                <h4 class="news-title">{{ news.title }}</h4>
                <p class="news-summary">{{ news.summary }}</p>
                <div class="news-meta">
                  <span class="news-date">{{ formatDate(news.publishTime) }}</span>
                  <span class="news-views">{{ news.viewCount }} 阅读</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="section-footer">
        <el-button type="primary" size="large" @click="goToNews">
          查看更多新闻
        </el-button>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import { preloadImage, handleImageError } from '@/utils/imageUtils'

export default {
  name: 'NewsSection',
  computed: {
    ...mapGetters(['featuredNews', 'latestNews'])
  },
  methods: {
    async getNewsImageUrl (news) {
      if (!news.coverImage) {
        return '/images/news-default.jpg'
      }
      return await preloadImage(news.coverImage, 'news')
    },
    goToNewsDetail (id) {
      this.$router.push(`/news/${id}`)
    },
    goToNews () {
      this.$router.push('/news')
    },
    formatDate (dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    },
    handleImageError (event) {
      return handleImageError(event, 'news')
    }
  }
}
</script>

<style lang="scss" scoped>
.news-section {
  padding: 80px 0;
  background: white;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: $text-primary;
  margin-bottom: 16px;
}

.section-subtitle {
  font-size: 1.1rem;
  color: $text-secondary;
  max-width: 600px;
  margin: 0 auto;
}

.news-content {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 60px;
  margin-bottom: 50px;
}

.subsection-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: $text-primary;
  margin-bottom: 24px;
  padding-bottom: 8px;
  border-bottom: 2px solid $primary-color;
  display: inline-block;
}

.featured-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.featured-item {
  background: white;
  border-radius: $border-radius-large;
  box-shadow: $box-shadow-base;
  overflow: hidden;
  transition: $transition-base;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: $box-shadow-light;
  }
}

.featured-image {
  position: relative;
  height: 200px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: $transition-base;
  }

  .featured-overlay {
    position: absolute;
    top: 12px;
    right: 12px;
  }

  .featured-tag {
    background: $primary-color;
    color: white;
    font-size: 0.8rem;
    padding: 4px 8px;
    border-radius: $border-radius-small;
    font-weight: 500;
  }
}

.featured-content {
  padding: 20px;
}

.featured-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: $text-primary;
  margin-bottom: 12px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.featured-summary {
  font-size: 0.9rem;
  color: $text-secondary;
  line-height: 1.6;
  margin-bottom: 16px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.featured-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  color: $text-secondary;
}

.news-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.news-item {
  display: flex;
  gap: 16px;
  padding: 16px;
  background: white;
  border-radius: $border-radius-base;
  box-shadow: $box-shadow-base;
  transition: $transition-base;
  cursor: pointer;

  &:hover {
    box-shadow: $box-shadow-light;
    transform: translateX(4px);
  }
}

.news-image {
  width: 80px;
  height: 80px;
  border-radius: $border-radius-small;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.news-content {
  flex: 1;
  min-width: 0;
}

.news-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: $text-primary;
  margin-bottom: 8px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-summary {
  font-size: 0.8rem;
  color: $text-secondary;
  line-height: 1.5;
  margin-bottom: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: $text-secondary;
}

.section-footer {
  text-align: center;
}

// 响应式设计
@media (max-width: 768px) {
  .news-section {
    padding: 60px 0;
  }

  .section-title {
    font-size: 2rem;
  }

  .news-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .featured-grid {
    grid-template-columns: 1fr;
  }

  .news-item {
    &:hover {
      transform: none;
    }
  }
}
</style>
